/**
 * Which top-level sidebar groups are open, and the one rule that changes the set.
 *
 * The menu used to be a strict accordion: opening a group closed whichever one was open.
 * When that group was ABOVE the one clicked, the header the person had just pressed jumped
 * up by the height of what collapsed (staging QA, 16 Oct: y=630 -> 309 -> 114 for the
 * owner's own case). The rule is now:
 *
 *   OPENING a group closes only the open groups positioned BELOW it.
 *
 * Groups above stay open, so nothing between the top of the menu and the header changes
 * height, and the header cannot move; a group closing below it only shortens what lies
 * beneath the pointer. Closing a group (clicking an open header) closes just that group.
 *
 * Positions are the groups' places in the menu, passed in as `order`. Keys are the groups'
 * language-independent ids (see keyOf in components/nav-main.tsx).
 */

/** Opens `key`; closes every open group that stands below it in `order`. */
export function openGroup(open: readonly string[], order: readonly string[], key: string): string[] {
  const at = order.indexOf(key)
  // A key the menu does not list has no place to be above or below: just add it.
  if (at < 0) return open.includes(key) ? [...open] : [...open, key]
  const above = open.filter((other) => {
    const place = order.indexOf(other)
    // A remembered group this menu no longer lists (another role, an old build) cannot be
    // shown, so it is forgotten rather than kept for ever.
    return place >= 0 && place < at
  })
  return [...above, key]
}

/** Closes `key` alone. */
export function closeGroup(open: readonly string[], key: string): string[] {
  return open.filter((other) => other !== key)
}

/**
 * The group to treat as "the one you are in" when a page lives under several: the last
 * (lowest) of the candidates that is open, else the first. The lowest open group is always
 * the one opened most recently, because opening a group closes everything below it.
 */
export function pickHomeGroup(candidates: readonly string[], open: readonly string[]): string | null {
  for (let i = candidates.length - 1; i >= 0; i--) {
    if (open.includes(candidates[i])) return candidates[i]
  }
  return candidates[0] ?? null
}

/** localStorage key holding the open set as a JSON array of group ids. */
export const OPEN_GROUPS_STORAGE_KEY = 'hf-app:sidebar:open-tops'
/** What the single-open menu stored: one id, or '' for none. Still read, never written. */
export const LEGACY_OPEN_GROUP_STORAGE_KEY = 'hf-app:sidebar:open-top'

/** The remembered set, or null when nothing was remembered (or storage is unavailable). */
export function readStoredOpenGroups(storage: Pick<Storage, 'getItem'>): string[] | null {
  try {
    const raw = storage.getItem(OPEN_GROUPS_STORAGE_KEY)
    if (raw !== null) {
      const parsed: unknown = JSON.parse(raw)
      if (Array.isArray(parsed)) return parsed.filter((k): k is string => typeof k === 'string' && k !== '')
    }
    const legacy = storage.getItem(LEGACY_OPEN_GROUP_STORAGE_KEY)
    if (legacy !== null) return legacy === '' ? [] : [legacy]
  } catch {
    // Private mode, quota, bad JSON: remember nothing.
  }
  return null
}
